import React, { useState, useEffect, useCallback } from 'react';
import {
    ChevronLeft,
    Loader2,
    Info,
    RefreshCcw,
    Building,
    ShoppingBag,
    User,
    CreditCard,
    ChevronDown,
    ChevronUp,
    CheckCircle
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import {
    currencyCodeToSymbolMap,
    fetchStoresWhereStaffAssigned,
} from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { useNavigate } from 'react-router-dom';
import FastAPIClient from '../services/apiClient';

const client = new FastAPIClient();

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '$';
    const num = parseFloat(amount) || 0;
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Math.abs(num));
    return `${num < 0 ? '-' : ''}${symbol}${formatted}`;
};

const statusBadge = (status) => {
    const base = 'inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider';
    if (status === 'pending') return `${base} bg-amber-100 text-amber-800`;
    if (status === 'approved' || status === 'completed' || status === 'posted') return `${base} bg-green-100 text-green-800`;
    if (status === 'rejected') return `${base} bg-red-100 text-red-800`;
    return `${base} bg-slate-100 text-slate-700`;
};

const PendingCashDeposits = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [deposits, setDeposits] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Filters matching OrganizationDatedWindowRequest
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [selectedCashierId, setSelectedCashierId] = useState('');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1); // Default to last 30 days
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            loadStores(parsedUser);
        }
    }, []);

    const loadStores = async (currentUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(currentUser);
            const mappedStores = fetchedStores.map(store => ({
                label: store.identifier,
                value: store.store_id,
            }));
            setStores(mappedStores);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to load stores.", "error");
        }
    };

    const fetchPendingDeposits = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const payload = {
                store_id: selectedStoreId ? parseInt(selectedStoreId) : null,
                cashier_id: selectedCashierId ? parseInt(selectedCashierId) : null,
                start_date: startDate || null,
                end_date: endDate || null,
            };

            const response = await client.postData('/accounting/pending-cash-deposits', payload);
            
            setDeposits(response?.items || []);
            setTotalCount(response?.count || 0);
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to fetch pending cash deposits.", "error");
            setDeposits([]);
        } finally {
            setLoading(false);
        }
    }, [user, selectedStoreId, selectedCashierId, startDate, endDate, showToast]);

    useEffect(() => {
        if (user) {
            fetchPendingDeposits();
        }
    }, [user, fetchPendingDeposits]);

    const handleApproveDeposit = async (transactionId) => {
        setActionLoadingId(transactionId);
        try {
            const payload = { transaction_id: transactionId };
            const response = await client.postData('/accounting/approve-cash-deposit', payload);
            
            showToast(response.msg || "Cash deposit approved and posted successfully.", "success");
            
            // Refresh list after approval
            await fetchPendingDeposits();
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to approve cash deposit.", "error");
        } finally {
            setActionLoadingId(null);
        }
    };

    const toggleRowExpansion = (transactionId) => {
        setExpandedRows(prev => ({
            ...prev,
            [transactionId]: !prev[transactionId]
        }));
    };

    return (
        <div className="p-4 md:p-8 bg-[#f8f9fa] min-h-screen text-slate-900">
            <div className="max-w-7xl mx-auto">
                <header className="mb-6">
                    <Button onClick={() => navigate(-1)} variant="ghost" className="p-0 h-auto mb-4 text-slate-500 hover:text-indigo-600 flex items-center">
                        <ChevronLeft size={16} /> <span className="text-xs uppercase tracking-wider font-bold">Back</span>
                    </Button>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Pending Cash Deposits</h1>
                            <p className="text-sm text-slate-500 font-medium">Review, monitor, and approve pending cash-to-bank transfers</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100">
                                Total Records: {totalCount}
                            </span>
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <div>
                            <SearchableSelectBox
                                label="Filter by Store"
                                options={stores}
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                placeholder="All Stores"
                                isClearable={true}
                                containerClassName="mb-0"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">From Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="mb-4 w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">To Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="mb-4 w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>

                        <div className="flex gap-2 mb-4">
                            <Button
                                onClick={fetchPendingDeposits}
                                disabled={loading}
                                className="bg-indigo-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-indigo-700"
                                title="Refresh"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <RefreshCcw size={18} />}
                            </Button>
                        </div>
                    </div>
                </header>

                {/* Main Content Table Card */}
                <Card className="overflow-hidden border-none shadow-xl rounded-2xl">
                    {loading ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Loader2 className="animate-spin mb-2" size={32} />
                            <p className="text-sm font-medium">Loading pending deposits...</p>
                        </div>
                    ) : deposits.length === 0 ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Info size={32} className="mb-2 opacity-20" />
                            <p className="text-sm font-medium">No pending cash deposits found for the selected criteria.</p>
                        </div>
                    ) : (
                        <div className="bg-white overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/80 border-b border-slate-100">
                                        <th className="px-4 py-4 w-10"></th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Date / Ref</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Store & Cashier</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Bank Destination</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Cash Source</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Amount</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-slate-50">
                                    {deposits.map((item) => {
                                        const isExpanded = !!expandedRows[item.transaction_id];
                                        const cashierFullName = [item.cashier_first_name, item.cashier_middle_name, item.cashier_last_name].filter(Boolean).join(' ') || `ID: ${item.cashier_id}`;
                                        const isPending = item.status?.toLowerCase() === 'pending';

                                        return (
                                            <React.Fragment key={item.transaction_id}>
                                                <tr className="hover:bg-slate-50/50 transition-colors group">
                                                    <td className="px-4 py-4 text-center">
                                                        <button
                                                            onClick={() => toggleRowExpansion(item.transaction_id)}
                                                            className="text-slate-400 hover:text-indigo-600 focus:outline-none"
                                                            title="Toggle Postings Breakdown"
                                                        >
                                                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-slate-800 text-xs font-bold font-mono">{item.ref}</div>
                                                        <div className="text-[10px] text-slate-400 mt-0.5">{new Date(item.transaction_date).toLocaleString()}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-bold text-slate-800 text-xs flex items-center mb-1">
                                                            <ShoppingBag size={12} className="mr-1 text-emerald-600" /> {item.store_identifier || `Store #${item.store_id}`}
                                                        </div>
                                                        <div className="text-[10px] text-slate-500 flex items-center">
                                                            <User size={12} className="mr-1 text-amber-600" /> {cashierFullName}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-700 text-xs flex items-center mb-0.5">
                                                            <CreditCard size={12} className="mr-1 text-indigo-500" /> {item.bank_account_name || 'Bank Account'}
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 font-mono">Code: {item.bank_account_code || item.bank_account_id}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-700 text-xs flex items-center mb-0.5">
                                                            <Building size={12} className="mr-1 text-slate-500" /> {item.cash_account_name || 'Cash Control'}
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 font-mono">Code: {item.cash_account_code || item.cash_account_id}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right font-mono font-bold text-slate-900">
                                                        {formatCurrency(item.amount, item.currency_code)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className={statusBadge(item.status)}>{item.status}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        {isPending ? (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3 inline-flex items-center gap-1.5"
                                                                disabled={actionLoadingId === item.transaction_id}
                                                                onClick={() => handleApproveDeposit(item.transaction_id)}
                                                            >
                                                                {actionLoadingId === item.transaction_id ? (
                                                                    <Loader2 size={14} className="animate-spin" />
                                                                ) : (
                                                                    <CheckCircle size={14} />
                                                                )}
                                                                Approve
                                                            </Button>
                                                        ) : (
                                                            <span className="text-xs text-slate-400">—</span>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Expanded Postings Breakdown Row */}
                                                {isExpanded && (
                                                    <tr className="bg-slate-50/80 border-b border-slate-200">
                                                        <td colSpan="8" className="px-8 py-4">
                                                            <div className="space-y-3">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                                                                        Transaction Postings & Journal Entries ({item.postings?.length || 0})
                                                                    </span>
                                                                    {item.description && (
                                                                        <span className="text-xs text-slate-500 italic">
                                                                            Description: {item.description}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                                                                    <table className="min-w-full text-xs">
                                                                        <thead className="bg-slate-100 text-slate-500 border-b border-slate-200">
                                                                            <tr>
                                                                                <th className="px-4 py-2 text-left font-bold uppercase tracking-wider">Account ID</th>
                                                                                <th className="px-4 py-2 text-left font-bold uppercase tracking-wider">Account Name & Code</th>
                                                                                <th className="px-4 py-2 text-center font-bold uppercase tracking-wider">Posting Type</th>
                                                                                <th className="px-4 py-2 text-right font-bold uppercase tracking-wider">Amount</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-slate-100 font-mono">
                                                                            {item.postings?.map((p, pIdx) => (
                                                                                <tr key={pIdx} className="hover:bg-slate-50">
                                                                                    <td className="px-4 py-2 font-bold text-slate-700">{p.account_id}</td>
                                                                                    <td className="px-4 py-2 font-sans font-medium text-slate-800">
                                                                                        {p.account_name || 'Account'} <span className="text-slate-400 font-mono text-[10px]">({p.account_code})</span>
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-center">
                                                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${p.posting_type === 'debit' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                                                                            {p.posting_type}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-right font-bold text-slate-900">
                                                                                        {formatCurrency(p.amount, item.currency_code)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default PendingCashDeposits;